// Email tab — email thread + compose box. Reuses the tenant-scoped
// /leads/:id/email send endpoint and the lead payload's email messages. A sent
// email is shown immediately (optimistic) and reconciled when the thread refetches.

import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../../../lib/api.js';
import { fmtDate, fmtFromNow } from '../../../lib/format.js';
import { EMAIL_TEMPLATES } from '../../../lib/emailTemplates.js';

const KEYWORDS = ['{{firstName}}', '{{lastName}}', '{{state}}'];

export default function EmailTab({ leadId, emailMessages = [], onChange }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]); // sent, not yet in the polled thread
  const bodyRef = useRef(null);

  // Drop optimistic rows once the server thread contains them.
  useEffect(() => {
    setPending((p) => p.filter((x) => !emailMessages.some(
      (m) => m.subject === x.subject && Math.abs(new Date(m.createdAt) - new Date(x.createdAt)) < 60_000,
    )));
  }, [emailMessages]);

  const thread = [...emailMessages, ...pending]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  function applyTemplate(id) {
    const t = EMAIL_TEMPLATES.find((x) => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body); }
  }

  function insertKeyword(k) {
    const el = bodyRef.current;
    const at = el?.selectionStart ?? body.length;
    setBody((b) => b.slice(0, at) + k + b.slice(at));
  }

  async function send() {
    if (!body.trim() || sending) return;
    if (!subject.trim()) { toast.error('Add a subject'); return; }
    setSending(true);
    try {
      await apiPost(`/leads/${leadId}/email`, { subject, body });
      setPending((p) => [{ _id: `pending-${Date.now()}`, subject, body, createdAt: new Date().toISOString(), pending: true }, ...p]);
      setSubject(''); setBody('');
      toast.success('Email sent');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  const field = `w-full rounded-lg bg-civix-panel border border-civix-border px-3 py-2
                 text-xs text-white/85 focus:border-civix-purple/60 outline-hidden`;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 p-1">
        {thread.length === 0 && <p className="text-xs text-white/40 p-3">No emails yet.</p>}
        {thread.map((m) => (
          <div key={m._id} className={`rounded-lg border px-3 py-2 ${m.direction === 'inbound' ? 'bg-slate-800/60 border-civix-border' : 'bg-civix-panel border-civix-border'}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-white/85">{m.subject || '(no subject)'}</span>
              <span className="text-[10px] text-white/40 shrink-0" title={m.createdAt ? fmtDate(m.createdAt) : ''}>
                {m.pending ? 'Just now' : fmtFromNow(m.createdAt)}
              </span>
            </div>
            <p className="text-[11px] text-white/60 mt-1 whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}
      </div>

      <div className="border-t border-civix-border pt-2 space-y-2">
        <select className={field} value="" onChange={(e) => applyTemplate(e.target.value)} aria-label="Preset reply">
          <option value="">Select a preset reply…</option>
          {EMAIL_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={field} />
        <div className="flex flex-wrap items-center gap-1 text-[10px] text-white/40">
          Keywords:
          {KEYWORDS.map((k) => (
            <button key={k} type="button" onClick={() => insertKeyword(k)} className="px-1.5 py-0.5 rounded bg-white/5 hover:bg-civix-purple/20 hover:text-white">{k}</button>
          ))}
        </div>
        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write an email…"
          rows={4}
          className={`${field} resize-none`}
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          className="btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send
        </button>
      </div>
    </div>
  );
}
